import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  addCompany,
  deleteCompany,
  fetchCompanies,
  updateCompany,
} from "redux/actions/company";
import CommonTable, { CommonTableProps } from "components/CommonTable";
import CompanyRes from "types/res/company/CompanyRes";
import styled from "styled-components";

import { FormType } from "components/FormField";
import UpdateCompanyReq from "types/req/company/UpdateCompanyReq";
import EnumUtils from "../../utils/EnumUtils";
import StaffRole from "../../types/enum/StaffRole";
import { Redirect } from "react-router-dom";
import { fetchCompanyGroups } from "redux/actions/companyGroup";
import CreateCompanyReq from "types/req/company/CreateCompanyReq";
import { CastExaminationType } from "types/enum/CastExaminationType";

const Company: React.FC = () => {
  const { t } = useTranslation("company");
  const dispatch = useDispatch();
  const companies: CompanyRes[] = useSelector((state) => state.company);
  const companyGroup = useSelector((state) => state.companyGroup);
  const role = useSelector((state) => state.account.staff.role);
  useEffect(() => {
    dispatch(fetchCompanies());
    dispatch(fetchCompanyGroups());
  }, [dispatch]);
  if (
    ![
      StaffRole.notelMaster,
      StaffRole.notelCallCenterEmployee,
      StaffRole.notelCallCenterPartTime,
    ].includes(
      EnumUtils.mapToEnum(StaffRole, role) || StaffRole.clientShopPartTime
    )
  ) {
    return <Redirect to="/" />;
  }
  const headers: { key: keyof CompanyRes; label: string }[] = [
    { key: "name", label: t("headers.name") },
    { key: "companyGroupId", label: t("headers.companyGroupId") },
    { key: "kana", label: t("headers.kana") },
    { key: "email", label: t("headers.email") },
    { key: "tel", label: t("headers.tel") },
    { key: "representativeName", label: t("headers.representativeName") },
    { key: "isCallCenterContract", label: t("headers.isCallCenterContract") },
    { key: "pointShopFlag", label: t("headers.pointShopFlag") },
    { key: "guestCategoryShopFlag", label: t("headers.guestCategoryShopFlag") },
    { key: "castExaminationType", label: t("headers.castExaminationType") },
    { key: "castExaminationCount", label: t("headers.castExaminationCount") },
    { key: "sort", label: t("headers.sort") },
    { key: "changeDateTime", label: t("headers.changeDateTime") },
    { key: "urlPageSmsEnabled", label: t("headers.urlPageSmsEnabled") },
  ];
  const forms = [
    {
      label: t("fields.name"),
      key: "name",
      type: FormType.Text,
    },
    {
      label: t("fields.companyGroup"),
      key: "companyGroupId",
      type: FormType.Select,
      options:
        companyGroup.map((c) => ({
          id: c.companyGroupId,
          name: c.name,
        })) || [],
    },
    {
      label: t("fields.kana"),
      key: "kana",
      type: FormType.Text,
    },
    {
      label: t("fields.email"),
      key: "email",
      type: FormType.Text,
    },
    {
      label: t("fields.tel"),
      key: "tel",
      type: FormType.Text,
    },
    {
      label: t("fields.representativeName"),
      key: "representativeName",
      type: FormType.Text,
    },
    {
      label: t("fields.isCallCenterContract"),
      key: "isCallCenterContract",
      type: FormType.Boolean,
      options: [
        { id: "0", name: t("options.unContracted") },
        { id: "1", name: t("options.contracted") },
      ],
    },
    {
      label: t("fields.pointShopFlag"),
      key: "pointShopFlag",
      type: FormType.Boolean,
      options: [
        { id: "0", name: t("options.bulkUse") },
        { id: "1", name: t("options.shopUse") },
      ],
    },
    {
      label: t("fields.guestCategoryShopFlag"),
      key: "guestCategoryShopFlag",
      type: FormType.Boolean,
      options: [
        { id: "0", name: t("options.bulk") },
        { id: "1", name: t("options.shop") },
      ],
    },
    {
      label: t("fields.castExaminationType"),
      key: "castExaminationType",
      type: FormType.Select,
      options: Object.entries(CastExaminationType).map(([key, value]) => ({
        id: key,
        name: value,
      })),
    },
    {
      label: t("fields.castExaminationCount"),
      key: "castExaminationCount",
      type: FormType.Number,
    },
    { label: t("fields.sort"), key: "sort", type: FormType.Number },
    { label: t("fields.changeDateTime"), key: "changeDateTime", type: FormType.Time },
    {
      label: t("fields.urlPageSmsEnabled"),
      key: "urlPageSmsEnabled",
      type: FormType.Boolean,
      options: [
        { id: "0", name: t("options.disabled") },
        { id: "1", name: t("options.enabled") },
      ],
    },
  ];

  return (
    <>
      <CompanyTable
        title={t("title")}
        rows={headers}
        data={companies}
        formId="companyForm"
        forms={forms}
        addFunc={addCompany}
        updateFunc={updateCompany}
        deleteFunc={(item) => deleteCompany(item?.companyId)}
        addType={CreateCompanyReq}
        updateType={UpdateCompanyReq}
        values={[
          (s) => s.name,
          (s) => s.companyGroup?.name ?? t("placeholders.noGroup"),
          (s) => s.kana,
          (s) => s.email,
          (s) => s.tel,
          (s) => s.representativeName,
          (s) => (s.isCallCenterContract ? t("options.contracted") : t("options.unContracted")),
          (s) => (s.pointShopFlag ? t("options.shopUse") : t("options.bulkUse")),
          (s) => (s.guestCategoryShopFlag ? t("options.shop") : t("options.bulk")),
          (s) =>
            s.castExaminationType
              ? EnumUtils.mapToEnum(
                  CastExaminationType,
                  s.castExaminationType
                ) || ""
              : "",
          (s) => s.castExaminationCount || "",
          (s) => s.sort || "",
          (s) => s.changeDateTime,
          (s) => (s.urlPageSmsEnabled ? t("options.enabled") : t("options.disabled")),
        ]}
      />
    </>
  );
};

const CompanyTable = styled<React.FC<CommonTableProps<CompanyRes>>>(
  CommonTable
)`
  margin-top: 24px;
`;

export default Company;
